import React, { useEffect, useState } from "react";
import { api } from "@/api/client";
import { useLang } from "@/context/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Plus, Search } from "lucide-react";
import JobDialog from "@/components/portal/JobDialog";
import { effectiveTotal } from "@/lib/jobActions";

export default function JobsPage() {
  const { t } = useLang();
  const [jobs, setJobs] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const fetchJobs = async () => {
    const res = await api.get("/jobs");
    setJobs(res.data);
  };

  useEffect(() => { fetchJobs(); }, []);

  const filtered = jobs.filter((j) => {
    const s = q.toLowerCase();
    return (
      j.client_name?.toLowerCase().includes(s) ||
      j.service_type?.toLowerCase().includes(s) ||
      j.address_from?.toLowerCase().includes(s) ||
      j.address_to?.toLowerCase().includes(s)
    );
  });

  return (
    <div data-testid="jobs-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight" style={{ fontFamily: "Outfit" }}>
            {t.portal.nav.jobs}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">{jobs.length} total</p>
        </div>
        <Button onClick={() => { setEditing(null); setOpen(true); }} className="rounded-full bg-primary hover:bg-primary/90 text-primary-foreground font-semibold" data-testid="jobs-add-btn">
          <Plus className="h-4 w-4 mr-1" /> {t.portal.addJob}
        </Button>
      </div>

      <div className="mt-6 relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input placeholder="Search client, service or address..." value={q} onChange={(e) => setQ(e.target.value)} className="pl-10 bg-card" data-testid="jobs-search-input" />
      </div>

      <div className="mt-6 rounded-xl border border-border overflow-hidden">
        <div className="hidden md:grid grid-cols-12 gap-2 px-4 py-3 bg-card border-b border-border text-xs uppercase tracking-widest text-muted-foreground">
          <div className="col-span-2">Date</div>
          <div className="col-span-3">Client</div>
          <div className="col-span-3">Service</div>
          <div className="col-span-2">Total</div>
          <div className="col-span-2 text-right">Status</div>
        </div>
        <div className="divide-y divide-border">
          {filtered.length === 0 && (
            <div className="p-8 text-center text-sm text-muted-foreground">No jobs yet</div>
          )}
          {filtered.map((j) => {
            const jc = j.status === "cancelled";
            return (
            <button
              key={j.id}
              onClick={() => { setEditing(j); setOpen(true); }}
              className={`w-full text-left grid md:grid-cols-12 gap-2 px-4 py-4 transition-colors ${jc ? "opacity-60 hover:bg-card/50" : "hover:bg-card"}`}
              data-testid={`jobs-row-${j.id}`}
            >
              <div className="md:col-span-2 text-sm text-muted-foreground">{j.date} · {j.start_time}</div>
              <div className={`md:col-span-3 font-semibold ${jc ? "line-through text-muted-foreground" : ""}`}>{j.client_name}</div>
              <div className={`md:col-span-3 text-sm text-muted-foreground truncate ${jc ? "line-through" : ""}`}>{j.service_type}</div>
              <div className={`md:col-span-2 font-bold ${jc ? "line-through text-muted-foreground" : "text-primary"}`}>${effectiveTotal(j).toFixed(2)}</div>
              <div className="md:col-span-2 md:text-right">
                <span className={`text-[10px] uppercase px-2 py-1 rounded-full ${j.status === "completed" ? "bg-emerald-500/10 text-emerald-400" : j.status === "cancelled" ? "bg-destructive/10 text-destructive" : "bg-primary/10 text-primary"}`}>
                  {j.status}
                </span>
              </div>
            </button>
            );
          })}
        </div>
      </div>

      <JobDialog open={open} onOpenChange={setOpen} job={editing} onSaved={fetchJobs} />
    </div>
  );
}
